/* eslint-disable jsx-a11y/label-has-associated-control */

// project imports
import FolderIcon from 'assets/images/icons/folder.svg';
import { getFilterTypeOptions } from 'helpers/objectFunctions';
import { dispatch, useSelector } from 'store';
import { getOpenCloseTicketsData, postQueryData, getMemberCodeFromId } from 'store/slices/internalCommenting';
import { openSnackbar } from 'store/slices/snackbar';
import SubCard from 'ui-component/cards/SubCard';
import AutoCompleteInput from 'ui-component/extended/autocomplete';
import AutoCompleteAssigneeSearch from './autoCompleteAssigneeSearch';
import { CONVERSATION_DROPDOWN, PRIORITY_FILTER_DROPDOWN_DATA, REFERENCE_ID_LIST } from './constant';

// material-ui imports
import { Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';

// third-party
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { devConsolelog } from 'helpers/utilityFunctions';
import { CONVERSATION_DROPDOWN_RAISE_NEW_QUERY } from '../member-priority/constant';

const validationSchema = yup.object({
    category: yup.number().required('Category is required.'),
    request_type: yup.object().nullable().required('Request Type is required.'),
    reference_code: yup.string().required('Reference Id is required.'),
    priority: yup.mixed().nullable().required('Priority is required.'),
    team: yup.object().nullable().required('Team is required.')
    // external_query_type: yup.string().required('Conversation with is required.')
});

const InternalQueryPopup = ({ handleModalOpen, selectedTab = 0, isRaiseNewQuery = false }) => {
    const theme = useTheme();
    let dataSelector;

    if (isRaiseNewQuery) {
        dataSelector = (state) => state?.memberPriority;
    } else {
        dataSelector = (state) => state?.internalCommenting;
    }
    const baseData = useSelector(dataSelector);
    let data;
    let additionalFiltersData;

    if (isRaiseNewQuery) {
        const { ticketData = {}, additionalFiltersData: additionalFilters = {} } = baseData || {};
        data = ticketData;
        additionalFiltersData = additionalFilters;
    } else {
        const { data: baseDataObject = {}, additionalFiltersData: additionalFilters = {} } = baseData || {};
        data = baseDataObject;
        additionalFiltersData = additionalFilters;
    }

    const { utilities = {} } = data || {};
    const { filters = [] } = utilities || {};
    const { tags: tagsFilterData = [], teams: teamsFilterData = [] } = additionalFiltersData || {};

    const handleSubmit = async (values) => {
        const newValues = { ...values };
        newValues.request_type = values?.request_type?.id;
        if (newValues.team) newValues.team = values?.team?.id;
        newValues[newValues?.request_type_key] = values.reference_code;
        newValues.query_type = selectedTab === 1 ? values?.external_query_type?.value : values?.query_type;
        newValues.priority_type = Number(values?.priority?.id) || 2;
        delete newValues.assigned_to_name;
        delete newValues.reference_code;
        delete newValues.external_query_type;
        delete newValues.priority;

        if (newValues?.request_type_key === 'member_id') {
            const memberCode = await dispatch(getMemberCodeFromId(newValues?.member_id));
            if (!memberCode) return;
            newValues.request_type_key = 'member_code';
            newValues.member_code = memberCode;
            delete newValues.member_id;
        }

        const { file, ...otherValues } = newValues;
        const formData = new FormData();
        if (file) {
            formData.append('file', file);
        }

        devConsolelog('dd23', otherValues);

        // Append other form data fields
        Object.entries(otherValues).forEach(([key, value]) => {
            if (key === 'tags') {
                const tagsArr = value.map((item, index) => item.id);
                formData.append('tags', JSON.stringify(tagsArr));
            } else formData.append(key, value);
        });

        await dispatch(postQueryData(formData));
        // await dispatch(getOpenCloseTicketsData('', { query_type: [{ id: 2, name: 2 }] }));
    };

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema,
        initialValues: {
            category: '',
            request_type: '',
            request_description_or_state: '',
            assigned_to: '',
            assigned_to_name: '',
            tags: [],
            team: '',
            request_type_key: 'booking_code',
            query_type: 2,
            external_query_type: isRaiseNewQuery ? CONVERSATION_DROPDOWN_RAISE_NEW_QUERY?.[2] : CONVERSATION_DROPDOWN?.[0],
            reference_code: '',
            file: '',
            priority: { id: '2', label: 'Medium' }
        },
        onSubmit: (values) => {
            handleSubmit(values);
            formik.resetForm();
            handleModalOpen();
        }
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mt: 2 }}>
                <Grid container item spacing={4}>
                    <Grid container item spacing={3}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel
                                    id="issue-category"
                                    sx={{
                                        color: formik.touched.category && formik.errors.category ? theme.palette.error.main : {}
                                    }}
                                >
                                    Issue Category
                                </InputLabel>
                                <Select
                                    id="issue_category"
                                    name="category"
                                    label="Issue Category"
                                    onChange={formik.handleChange}
                                    value={formik.values.category}
                                    error={Boolean(formik.touched.category && formik.errors.category)}
                                >
                                    {getFilterTypeOptions('category', filters).map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.category && formik.errors.category ? (
                                    <FormHelperText sx={{ color: theme.palette.orange.dark }}>
                                        {formik.touched.category && formik.errors.category}
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <AutoCompleteInput
                                label="Request Type"
                                id="request_type"
                                disableClearable
                                options={getFilterTypeOptions('request_type', filters)}
                                value={formik.values.request_type}
                                error={formik.touched.request_type && Boolean(formik.errors.request_type)}
                                helperText={formik.touched.request_type && formik.errors.request_type}
                                isMultiSelect={false}
                                onChange={(event, newValue) => {
                                    const tempFilterTypeObj = {};
                                    if (newValue) {
                                        tempFilterTypeObj[newValue?.id] = newValue;
                                    }

                                    formik.setFieldValue('request_type', newValue);
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item spacing={3}>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel id="issue-category">Reference Id</InputLabel>
                                <Select
                                    id="request_type_key"
                                    name="request_type_key"
                                    label="Reference Id"
                                    onChange={formik.handleChange}
                                    value={formik.values.request_type_key}
                                >
                                    {REFERENCE_ID_LIST.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                id="reference_id"
                                name="reference_code"
                                onChange={formik.handleChange}
                                value={formik.values.reference_code}
                                error={formik.touched.reference_code && Boolean(formik.errors.reference_code)}
                                helperText={formik.touched.reference_code && formik.errors.reference_code}
                            />
                        </Grid>
                    </Grid>
                    {selectedTab === 1 && (
                        <Grid container item>
                            <Grid item xs={12}>
                                <AutoCompleteInput
                                    label="Conversation visible to"
                                    id="external_query_type"
                                    isMultiSelect={false}
                                    disableClearable
                                    error={formik.touched.external_query_type && Boolean(formik.errors.external_query_type)}
                                    helperText={formik.touched.external_query_type && formik.errors.external_query_type}
                                    options={isRaiseNewQuery ? CONVERSATION_DROPDOWN_RAISE_NEW_QUERY : CONVERSATION_DROPDOWN}
                                    value={formik.values.external_query_type}
                                    onChange={(event, newValue) => {
                                        // devConsolelog('inside autocomplete22', newValue);
                                        formik.setFieldValue('external_query_type', newValue);
                                        formik.setFieldValue('query_type', newValue.value);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {/* selectedTab */}
                    <Grid item xs={6}>
                        <AutoCompleteInput
                            label="Team"
                            id="team"
                            isMultiSelect={false}
                            disableClearable
                            options={teamsFilterData}
                            value={formik.values.team}
                            error={formik.touched.team && Boolean(formik.errors.team)}
                            helperText={formik.touched.team && formik.errors.team}
                            onChange={(event, newValue) => {
                                formik.setFieldValue('team', newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <AutoCompleteAssigneeSearch formik={formik} />
                    </Grid>
                    <Grid item xs={6}>
                        <AutoCompleteInput
                            label="Tags"
                            id="tags"
                            isMultiSelect
                            options={tagsFilterData}
                            values={formik?.values.tags}
                            onChange={(event, newValue) => {
                                formik.setFieldValue('tags', newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <AutoCompleteInput
                            label="Priority"
                            id="priority"
                            options={PRIORITY_FILTER_DROPDOWN_DATA}
                            getOptionLabel={(option) => option.label}
                            value={formik?.values?.priority}
                            onChange={(event, newValue) => {
                                formik.setFieldValue('priority', newValue);
                            }}
                            error={formik.touched.priority && Boolean(formik.errors.priority)}
                            helperText={formik.touched.priority && formik.errors.priority}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            id="issue-description"
                            name="request_description_or_state"
                            label="Describe Your Issue"
                            multiline
                            rows={3}
                            onChange={formik.handleChange}
                            value={formik.values.request_description_or_state}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SubCard
                            sx={{
                                height: '130px',
                                backgroundColor: theme.palette.primary.light,
                                border: `1px dashed ${theme.palette.primary.main}`,
                                boxShadow: 'none',
                                borderColor: theme.palette.primary.main,
                                ':hover': {
                                    boxShadow: 'none'
                                }
                            }}
                            contentSX={{ height: '100%' }}
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                sx={{ gap: '10px', height: '100%', cursor: 'pointer' }}
                            >
                                <img src={FolderIcon} alt="folder" />
                                <Typography variant="h6">Attach Documents</Typography>
                                <input
                                    id="fileInput"
                                    type="file"
                                    name="file"
                                    onChange={(e) => {
                                        const file = e.currentTarget.files[0];
                                        if (file?.size > 2000000) {
                                            formik.setFieldValue('file', null);
                                            e.currentTarget.value = '';
                                            dispatch(
                                                openSnackbar({
                                                    open: true,
                                                    message: 'File cannot be greater than 2MB',
                                                    variant: 'alert',
                                                    alert: {
                                                        color: 'error'
                                                    },
                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                    transition: 'SlideLeft',
                                                    close: true
                                                })
                                            );
                                            return;
                                        }
                                        formik.setFieldValue('file', file);
                                    }}
                                    accept=".jpg,.png,.pdf"
                                    style={{ marginLeft: '80px' }}
                                />
                            </Box>
                        </SubCard>
                    </Grid>
                    <Grid container item justifyContent="center">
                        <Grid item>
                            <Button variant="contained" type="submit" sx={{ mt: 2, width: '160px' }} id="SubmitButton">
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
};

InternalQueryPopup.propTypes = {
    handleModalOpen: PropTypes.func,
    selectedTab: PropTypes.number
};

export default InternalQueryPopup;
